import { ProfilePipe } from './../../shared/pipes/profile.pipe';
import { Component, OnDestroy, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { User } from 'src/app/models/User';
import { Promotion } from 'src/app/models/Promotion';

import { UserFilter } from './../../models/UserFilter';
import { ConfirmationService } from './../../shared/components/confirmation-modal/confirmation.service';
import { ToastService } from './../../shared/components/toast/toast.service';
import { PaginatedResult } from './../../shared/model/paginated-result';
import { UserService, ADMINISTRATOR } from './../../shared/services/user.service';
import { AuthService } from '../../shared/services/auth.service';
import { PromotionService } from '../../shared/services/promotion.service';
import { Subject, Subscription, of } from "rxjs";
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Shortener } from 'src/app/models/Shortener';
import { ShortenerService } from 'src/app/shared/services/shortener.service';
import { ProviderAccountService } from 'src/app/shared/services/provider-account.service';
import { ProviderAccount } from 'src/app/models/ProviderAccount';
import { FractionationService } from 'src/app/shared/services/fractionations.service';
import { FractionationRoute } from 'src/app/models/Fractionation';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { AvailableDumpsResponse, MigrationDump, MigrationService } from 'src/app/shared/services/migration.service';
import { AdminWalletChannelCost, AdminWalletChannelCosts, AdminWalletSummary, WALLET_PRICE_SOURCE_LABELS } from 'src/app/models/Wallet';
import { WalletTransferService } from 'src/app/shared/services/wallet-transfer.service';
import { MONEY_BALANCE_FRACTION_DIGITS, MONEY_DEFAULT_LOCALE, MoneyMicrosPipe } from 'src/app/shared/pipes/money-micros.pipe';

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
})
export class UsersComponent implements OnInit, OnDestroy {
  page = 1;
  perPage = 15;
  filter: string = null;
  promocodeId: string = "";
  active: boolean;
  validated: boolean;
  promocode: boolean;
  activeUser: User;
  result: PaginatedResult<User[]>;
  promotions: Promotion[] = [];
  invertedOrder: boolean;
  orderBy: string;

  loggedInUser: User;
  subscriptions: Subscription[] = [];

  automationId = "";
  broadcastFractionationRoute = "";
  shortenerId = "";

  providers: ProviderAccount[] = [];
  shorteners: Shortener[] = [];
  fractionationGroups: FractionationRoute[] = [];

  /**
   * Saldo da carteira por usuário da página atual (task 17tm466nz8b — pedido do Bruno em
   * 17/10), carregado numa chamada só depois de `listPaginated()` resolver. Chave ausente
   * = ainda carregando (ou usuário fora da carteira, que nunca entra aqui).
   */
  walletByUser: Record<string, AdminWalletSummary> = {};
  private walletBalancesSubscription: Subscription | null = null;
  /**
   * Preço atual e gasto do mês de cada canal cobrado pela carteira, por usuário (subtask
   * 17tm466vyfb, pedido do Misael em 30/10). Chamada própria, depois do saldo: o saldo não
   * espera pelas leituras de preço. Chave ausente = ainda carregando.
   */
  walletCostsByUser: Record<string, AdminWalletChannelCosts> = {};
  private walletCostsSubscription: Subscription | null = null;
  private readonly money = new MoneyMicrosPipe();
  /** 2 casas fixas, como o card de saldo da Minha Carteira (decisão do Bruno, 18/09). */
  readonly walletBalanceLocale = MONEY_DEFAULT_LOCALE;
  readonly walletBalanceFractionDigits = MONEY_BALANCE_FRACTION_DIGITS;

  @ViewChild('importMigrationModal') importMigrationModalTpl!: TemplateRef<unknown>;
  importMigrationRef: NgbModalRef | null = null;
  importEmail = '';
  importDumps: MigrationDump[] = [];
  importSelectedPrefix = '';
  importLoadingDumps = false;
  loading = false;
  importSubmitting = false;
  importLastSearchedEmail = '';
  importSearchError: string | null = null;
  // F3 — step de confirmação. 'select' = lista de dumps; 'review' = resumo
  // antes de enfileirar. Trocado via avançar/voltar dentro do modal.
  importStep: 'select' | 'review' = 'select';
  // F4 — quando o dump selecionado já tem imported=true, exigir toggle
  // explícito antes de mandar force=true pro backend.
  importForce = false;
  private importEmail$ = new Subject<string>();
  private importModalClose$ = new Subject<void>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private userService: UserService,
    private toastService: ToastService,
    private profilePipe: ProfilePipe,
    private confirmationService: ConfirmationService,
    private promotionService: PromotionService,
    private shortenerService: ShortenerService,
    private providerService: ProviderAccountService,
    private activatedRoute: ActivatedRoute,
    private fractionationService: FractionationService,
    private modalService: NgbModal,
    private migrationService: MigrationService,
    private walletTransferService: WalletTransferService
  ) { }

  get isAdmin(): boolean {
    return this.loggedInUser?.profile_id === ADMINISTRATOR;
  }

  get isAdministrator(): boolean {
    return this.activeUser?.profile_id === ADMINISTRATOR;
  }

  private static readonly WALLET_CHANNEL_LABELS: Record<string, string> = {
    sms: 'SMS',
    call: 'Ligação',
    whatsapp: 'WhatsApp',
    ai: 'IA',
    rcs: 'RCS',
  };

  private static readonly WALLET_CHANNEL_ICONS: Record<string, string> = {
    sms: 'fa-commenting-o',
    call: 'fa-phone',
    whatsapp: 'fa-whatsapp',
    ai: 'fa-magic',
    rcs: 'fa-mobile',
  };

  /** Unidade do preço, curta (o card é pequeno); a frase inteira fica na dica. */
  private static readonly WALLET_CHANNEL_UNITS: Record<string, string> = {
    sms: '/SMS',
    call: '/min',
    whatsapp: '/msg',
    ai: '/mil tokens',
    rcs: '/RCS',
  };

  /** Preço unitário com até 4 casas: IA custa frações de centavo por mil tokens. */
  private static readonly UNIT_PRICE_FRACTION_DIGITS = 4;

  /** "SMS · Ligação" — só os canais desta conta que saem da carteira, nessa ordem fixa. */
  walletChargedChannels(billing: AdminWalletSummary['billing']): string {
    if (!billing) {
      return '—';
    }

    const labels = Object.entries(billing)
      .filter(([, source]) => source === 'wallet')
      .map(([channel]) => UsersComponent.WALLET_CHANNEL_LABELS[channel] ?? channel);

    return labels.length > 0 ? labels.join(' · ') : '—';
  }

  walletChannelLabel(channel: string): string {
    return UsersComponent.WALLET_CHANNEL_LABELS[channel] ?? channel;
  }

  walletChannelIcon(channel: string): string {
    return UsersComponent.WALLET_CHANNEL_ICONS[channel] ?? 'fa-circle-o';
  }

  walletChannelUnit(cost: AdminWalletChannelCost): string {
    return UsersComponent.WALLET_CHANNEL_UNITS[cost.channel] ?? '';
  }

  /**
   * "R$ 0,15" — ou a faixa "R$ 0,0070–0,0352" quando o canal tem dois preços (IA: entrada
   * e saída; RCS: Basic e Single). "—" quando a carteira não deu preço: nunca R$ 0,00.
   */
  walletChannelPrice(cost: AdminWalletChannelCost): string {
    const hasFrom = cost.unit_price_micros !== null && cost.unit_price_micros !== undefined;
    const hasTo = cost.unit_price_to_micros !== null && cost.unit_price_to_micros !== undefined;

    if (!hasFrom && !hasTo) {
      return '—';
    }

    // Só um dos dois preços veio (ex.: RCS sem Basic, mas com Single): mostra o que existe,
    // e a dica diz qual é qual.
    if (!hasFrom || !hasTo) {
      return this.unitPrice(hasFrom ? cost.unit_price_micros : cost.unit_price_to_micros);
    }

    const from = this.unitPrice(cost.unit_price_micros);
    const to = cost.unit_price_to_micros;

    if (to === cost.unit_price_micros) {
      return from;
    }

    // Só o número na ponta de cima: "R$ 0,0070–0,0352" cabe no card de 400 px.
    return `${from}–${this.unitPrice(to).replace(/^[^\d-]+/, '')}`;
  }

  /** A frase inteira do preço, com a origem (contrato, assinante, padrão). */
  walletChannelPriceHint(cost: AdminWalletChannelCost): string {
    const noPrice = (cost.unit_price_micros === null || cost.unit_price_micros === undefined)
      && (cost.unit_price_to_micros === null || cost.unit_price_to_micros === undefined);

    if (noPrice) {
      return cost.price_unavailable
        ? 'Não foi possível consultar o preço na carteira agora.'
        : 'Sem preço definido na carteira para este canal agora.';
    }

    const source = cost.price_source ? WALLET_PRICE_SOURCE_LABELS[cost.price_source] : null;
    const price = (micros: number | null) => micros === null || micros === undefined ? '—' : this.unitPrice(micros);
    let text: string;

    switch (cost.channel) {
      case 'ai':
        text = `Entrada ${price(cost.unit_price_micros)} e saída ${price(cost.unit_price_to_micros)} por mil tokens`
          + (cost.variant ? ` (modelo ${cost.variant.split(':').pop()})` : '');
        break;
      case 'rcs':
        text = `Basic ${price(cost.unit_price_micros)} e Single ${price(cost.unit_price_to_micros)} por mensagem`;
        break;
      case 'call':
        text = `${price(cost.unit_price_micros)} por minuto de ligação (cobrado em blocos de 30 s)`;
        break;
      case 'whatsapp':
        text = `${price(cost.unit_price_micros)} por mensagem de WhatsApp`;
        break;
      default:
        text = `${price(cost.unit_price_micros)} por SMS enviado`;
    }

    return source ? `${text} · ${source}` : text;
  }

  /** Gasto do período com 2 casas, como o "Gasto por canal" do painel. "—" = não sei. */
  walletChannelSpent(cost: AdminWalletChannelCost): string {
    if (cost.spent_micros === null || cost.spent_micros === undefined) {
      return '—';
    }

    return this.money.transform(cost.spent_micros, 'BRL', this.walletBalanceLocale, this.walletBalanceFractionDigits, this.walletBalanceFractionDigits);
  }

  /** "01/11 a 02/11" — o período vem em dias de Brasília (Y-m-d). */
  walletCostsPeriod(costs: AdminWalletChannelCosts): string {
    if (!costs.period) {
      return '';
    }

    const day = (iso: string) => iso.split('-').reverse().slice(0, 2).join('/');

    return `${day(costs.period.from)} a ${day(costs.period.to)}`;
  }

  private unitPrice(micros: number): string {
    return this.money.transform(micros, 'BRL', this.walletBalanceLocale, UsersComponent.UNIT_PRICE_FRACTION_DIGITS);
  }

  ngOnInit(): void {
    this.promotionService
      .listAll()
      .subscribe(promotions => this.promotions = promotions);

    this.shortenerService
      .listAll()
      .subscribe(shorteners => this.shorteners = shorteners);

    this.providerService
      .listAll()
      .subscribe(providers => this.providers = providers);

    this.fractionationService
      .getRoutes(0, 9999)
      .subscribe({
        next: (data: FractionationRoute[]) => {
              this.fractionationGroups = data;
        },
        error: (error) => {
          this.toastService.error('Erro ao buscar as rotas');
        }
      });

    this.activatedRoute.queryParams.subscribe((params) => {
      this.perPage = +params?.per_page || this.perPage;
      this.page = +params?.page || this.page;
      this.filter = params.filter || this.filter;
      this.promocodeId = params.promocodeId || this.promocodeId;
      this.active = null;
      this.validated = null;
      this.promocode = null;
      this.orderBy = params.orderBy || this.orderBy;
      this.automationId = params.automation_id ?? "";
      this.broadcastFractionationRoute = params.broadcast_fractionation_route || this.broadcastFractionationRoute;
      this.shortenerId = params.shortener_id || this.shortenerId;

      if (params.active) {
        this.active = JSON.parse(params.active);
      }

      if (params.validated) {
        this.validated = JSON.parse(params.validated);
      }

      if (params.promocode) {
        this.promocode = JSON.parse(params.promocode);
      }

      this.listPaginated();
      this.activeUser = this.authService.getUser();
    });

    this.loggedInUser = this.authService.getLoggedInUser();
  }


  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.walletBalancesSubscription?.unsubscribe();
    this.walletCostsSubscription?.unsubscribe();
    this.importEmail$.complete();
    this.importModalClose$.complete();
  }

  listPaginated(): void {
    this.invertedOrder = this.route.queryParams['_value']['invertedOrder'];

    if (this.invertedOrder) {
      this.invertedOrder = JSON.parse(this.invertedOrder.toString())
    }

    this.orderBy = this.route.queryParams['_value']['orderBy'];

    let order = this.orderBy;

    let inverted = this.invertedOrder;

    const filter: UserFilter = {
      active: this.active,
      validated: this.validated,
      promocode: this.promocode,
      text: this.filter,
      promocodeId: this.promocodeId,
      automationId: this.automationId,
      broadcastFractionationRoute: this.broadcastFractionationRoute,
      shortenerId: this.shortenerId
    };

    this.loading = true;
    this.subscriptions.push(
      this.userService
      .listPaginated(this.page, this.perPage, filter, order, inverted)
      .subscribe({
        next: (result) => {
          this.loading = false;
          this.result = result;
          this.loadWalletBalances(result?.data ?? []);
        },
        error: () => { this.loading = false; },
      })
    );
  }

  /**
   * Saldo da carteira dos usuários já migrados DESTA página, numa chamada só (task
   * 17tm466nz8b). Cancela a chamada da página anterior antes de disparar a nova: sem
   * isso, uma resposta atrasada da página de antes pintaria saldo errado por cima da
   * página atual (troca rápida de página/filtro).
   */
  private loadWalletBalances(users: User[]): void {
    this.walletBalancesSubscription?.unsubscribe();
    this.walletCostsSubscription?.unsubscribe();
    this.walletByUser = {};
    this.walletCostsByUser = {};

    const migratedIds = users
      .filter(u => u.wallet_billing_enabled || u.wallet_sms_billed || u.wallet_call_billed)
      .map(u => u.id);

    this.walletBalancesSubscription = this.walletTransferService
      .balances(migratedIds)
      .subscribe({
        next: (response) => { this.walletByUser = response.data; },
        error: () => {
          this.walletByUser = migratedIds.reduce((acc, id) => {
            acc[id] = { status: 'unavailable', balance: null, billing: null };
            return acc;
          }, {} as Record<string, AdminWalletSummary>);
        },
      });

    // Custos por canal (17tm466vyfb): falha aqui não mexe no saldo — o card volta a
    // listar só os nomes dos canais, com o aviso.
    this.walletCostsSubscription = this.walletTransferService
      .channelCosts(migratedIds)
      .subscribe({
        next: (response) => { this.walletCostsByUser = response.data; },
        error: () => {
          this.walletCostsByUser = migratedIds.reduce((acc, id) => {
            acc[id] = { status: 'unavailable', period: null, channels: [] };
            return acc;
          }, {} as Record<string, AdminWalletChannelCosts>);
        },
      });
  }

  filterByAutomation(): void {
    this.pageChanged(this.page);
  }

  filterByBroadcastFractionationRoute(): void {
    this.pageChanged(this.page);
  }

  filterByShortener(): void {
    this.pageChanged(this.page);
  }

  filterKeyPress(event: KeyboardEvent): void {

    const key = event?.key?.toLocaleUpperCase();
    if (key === 'ENTER') {
      this.pageChanged(this.page);
    }
  }

  filterByStatus(active: boolean = null, promocode: boolean = null, validated: boolean = null): void {

    this.page = 1;
    this.active = active;
    this.validated = validated;
    this.promocode = promocode;

    this.pageChanged(this.page);
  }

  getTitle(user: User): string {
    let title = user.name;

    if (user.validated) {
      title = `[Verificado] ${title}`;
    }

    if (user.promocode) {
      title += ` (Promocode: ${user.promocode})`;
    }

    title += ' (Perfil: ' + this.profilePipe.transform(user.profile_id) + ')';

    return title;
  }

  simulate(user: User): void {
    this.authService.setSimulatedUser(user);
    this.router.navigate(['/']);
  }

  invertOrder() {
    this.router.navigate(['.'], {
      relativeTo: this.route,
      queryParams: {
        invertedOrder: !this.invertedOrder
      },
      queryParamsHandling: 'merge'
    })
  };

  orderByChanged() {
    this.router.navigate(['.'], {
      relativeTo: this.route,
      queryParams: {
        orderBy: this.orderBy
      },
      queryParamsHandling: 'merge'
    })
  }

  validate(user: User, validate: boolean): void {
    const action = validate ? 'validar' : 'remover a validação de';
    const confirmationMessage = `Deseja realmente ${action} o usuário <b>${user.name}</b> para enviar SMSs?`;
    const successMessage = validate ? 'Usuário validado com sucesso. Agora ele pode enviar SMS' : 'Validação removida com sucesso. O usuário não pode mais enviar SMS';

    this.confirmationService.show(confirmationMessage, () => {
      if (validate) {
        user.massive_sms_enabled = true;
      }

      this.subscriptions.push(
        this.userService
          .validate(user.id)
          .subscribe(
            () => {
              if (validate) {
                this.userService.save(user).subscribe(
                  (savedUser) => {
                    user = savedUser;
                    this.toastService.success(successMessage);
                    this.listPaginated();
                  },
                  (err) => this.toastService.error(err?.error?.message || `Erro ao ${action} usuário!`)
                );
              } else {
                this.toastService.success(successMessage);
                this.listPaginated();
              }
            },
            err => this.toastService.error(err.error.message || `Erro ao ${action} usuário!`)
          )
      );
    });
  }

  openImportMigrationModal(): void {
    this.importEmail = '';
    this.importDumps = [];
    this.importSelectedPrefix = '';
    this.importLoadingDumps = false;
    this.importSubmitting = false;
    this.importLastSearchedEmail = '';
    this.importSearchError = null;
    this.importStep = 'select';
    this.importForce = false;
    this.importMigrationRef = this.modalService.open(this.importMigrationModalTpl, {
      size: 'lg',
      ariaLabelledBy: 'importMigrationTitle',
    });

    // Pipe RxJS para busca de dumps no S3 ao digitar email no modal de import.
    // switchMap cancela request anterior se o admin continuar digitando, eliminando
    // a race condition onde uma resposta antiga sobrescrevia importDumps.
    //
    // Subscrito por abertura do modal e cancelado em takeUntil(importModalClose$):
    // se o admin fecha antes da debounce de 500ms expirar, ou da request voltar,
    // os state-writes em tap() / subscribe() não disparam contra um modal já fechado
    // (e numa próxima abertura, a subscription começa limpa).
    this.importEmail$.pipe(
      takeUntil(this.importModalClose$),
      debounceTime(500),
      map(email => email.trim()),
      filter(email => email.length > 0 && email.indexOf('@') !== -1),
      distinctUntilChanged(),
      tap(email => {
        this.importLoadingDumps = true;
        this.importLastSearchedEmail = email;
        this.importSearchError = null;
        this.importDumps = [];
        this.importSelectedPrefix = '';
      }),
      switchMap(email =>
        this.migrationService.availableDumps(email).pipe(
          catchError(err => {
            this.importSearchError = err?.error?.message || 'Erro ao buscar dumps no S3.';
            return of(null as AvailableDumpsResponse | null);
          })
        )
      )
    ).subscribe(res => {
      this.importLoadingDumps = false;
      if (res === null) return; // erro já tratado
      this.importDumps = res.dumps || [];
      if (this.importDumps.length === 1) {
        this.importSelectedPrefix = this.importDumps[0].s3_prefix;
      }
    });

    // NgbModalRef.result resolve em close() e rejeita em dismiss(); ambos
    // sinalizam fechamento, então emitir importModalClose$ pelos dois caminhos.
    this.importMigrationRef.result.then(
      () => this.importModalClose$.next(),
      () => this.importModalClose$.next(),
    );
  }

  get importSelectedDump(): MigrationDump | undefined {
    return this.importDumps.find(d => d.s3_prefix === this.importSelectedPrefix);
  }

  /**
   * trackBy keys for *ngFor — sem isso o Angular destrói/recria os <input>
   * radios a cada mudança de importDumps[], o que cancela seleção em curso
   * quando uma busca digita-debounce-emite no meio do clique.
   */
  trackByDumpPrefix(_i: number, dump: MigrationDump): string {
    return dump.s3_prefix;
  }

  trackByCountKey(_i: number, kv: { key: string; value: unknown }): string {
    return kv.key;
  }

  goToImportReview(): void {
    if (!this.importSelectedDump) {
      this.toastService.error('Selecione um dump.');
      return;
    }
    this.importForce = false;
    this.importStep = 'review';
  }

  backToImportSelect(): void {
    this.importStep = 'select';
    this.importForce = false;
  }

  onImportEmailChange(): void {
    const email = (this.importEmail || '').trim();
    // Reset estados imediatamente: o pipe debounce vai disparar a busca real
    // só quando o usuário parar de digitar 500ms; até lá, a UI fica limpa.
    this.importDumps = [];
    this.importSelectedPrefix = '';
    if (!email || email.indexOf('@') === -1) {
      this.importLastSearchedEmail = '';
      this.importSearchError = null;
      this.importLoadingDumps = false;
      return;
    }
    this.importEmail$.next(email);
  }

  confirmImportMigration(): void {
    const dump = this.importSelectedDump;
    if (!dump) {
      this.toastService.error('Selecione um dump.');
      return;
    }
    if (dump.imported && !this.importForce) {
      this.toastService.error('Este dump já foi importado. Marque "reimportar mesmo assim" antes.');
      return;
    }
    this.importSubmitting = true;
    // finalize garante reset do flag mesmo em caminhos não-felizes (unsubscribe
    // por ngOnDestroy, throw inesperado): sem ele, o botão de import ficava
    // travado em "Enviando..." quando o componente era destruído mid-flight.
    this.subscriptions.push(
      this.migrationService.import({
        email: this.importEmail.trim(),
        s3_prefix: dump.s3_prefix,
        call4u_user_id: dump.call4u_user_id,
        force: dump.imported ? this.importForce : undefined,
      })
        .pipe(finalize(() => (this.importSubmitting = false)))
        .subscribe({
          next: (res) => {
            this.toastService.success(res.message || 'Importação enfileirada.');
            this.importMigrationRef?.close();
            this.listPaginated();
          },
          error: (err) => {
            this.toastService.error(err?.error?.message || 'Erro ao iniciar importação.');
          }
        })
    );
  }

  pageChanged(page: any): void {
    const queryParams = {
      page,
      perPage: this.perPage,
      active: this.active,
      validated: this.validated,
      promocode: this.promocode,
      filter: this.filter,
      promocodeId: this.promocodeId,
      orderBy: this.orderBy,
      invertedOrder: this.invertedOrder,
      automation_id: this.automationId,
      broadcast_fractionation_route: this.broadcastFractionationRoute,
      shortener_id: this.shortenerId
    };

    if (!this.filter) {
      delete queryParams.filter;
    }

    this.router.navigate(['/users'], { queryParams });
  }
}
